// P1-3: what does this scan mean? One pure function, one answer.
//
// ── WHY THIS EXISTS ──────────────────────────────────────────────────────────
// Duplicate detection used to be spread across three places that disagreed:
//
//   * the serial filter effect in AssetManagement.js compared serials with all
//     whitespace stripped, and decided whether to collect at all;
//   * `collectAsset` re-derived the match itself with raw `===`, so a serial the
//     effect had matched could fail to match here;
//   * the duplicate check then looked up `newAssetId` -- whatever the effect had
//     written into *form state* -- rather than the row it had just resolved, so
//     the two could point at different assets.
//
// On top of that, only the typed-Enter path ever reached `collectAsset`: a gun
// scan that matched zero rows, or two, fell through a silent else and cleared
// four form fields with no message at all.
//
// The result depended on keydown timing, selection state and which browser was
// rendering -- which is what "worked in Edge, not in Chrome" during the
// 03/06/2026 test call actually meant. So the decision moves here: no React, no
// redux, no timing, no I/O. Same inputs, same answer, every browser.
//
// ── SCOPE (spec §5.2, §5.3) ──────────────────────────────────────────────────
// Matching is per inbound order. Serials are NEVER compared across orders -- the
// same serial legitimately appears on many orders -- so `assetsInOrder` filters
// first and everything else works on that subset. This is enforced here rather
// than trusted to whatever the redux slice happens to hold.

// Serial identity. Decision 1 (2026-08-05): strip ALL whitespace and uppercase,
// so `ABC 123` and `ABC123` are the same asset. A barcode reader dropping a
// printed space is the likelier real-world failure than two genuinely different
// serials colliding this way.
//
// ⚠ MUST AGREE WITH CC_Backend/utils/serials.js -> serialKey and with the SQL
// form of it (SERIAL_KEY_SQL in CC_Backend/data/assets.js). The frontend and
// backend implement this rule in separate packages -- CRA's ModuleScopePlugin
// forbids importing from outside src/, and the backend deploy artifact is
// CC_Backend only -- so their test files share one case table and say so. If the
// two ever drift, the client can decide two rows are one asset while the server
// disagrees, and then collect the wrong physical row.
const NBSP = / /g;

const serialKey = (value) => (value == null ? '' : String(value))
    .replace(NBSP, ' ')
    .replace(/\s+/g, '')
    .toUpperCase();

// Asset tags are the same kind of scanned/typed identifier, so they normalise the
// same way. (Tag matching is a V2 concern per the plan; the tiers below keep the
// behaviour the old code had rather than extending it.)
const tagKey = serialKey;

// `collected` and `unexpected` are nullable in the schema (DEFAULT false, no NOT
// NULL). Never compare to false -- that silently drops NULL rows, the bug P1-2
// found in every client-side count.
const isCollected = (asset) => asset != null && asset.collected === true;

// spec §5.2/§5.3. Loose id comparison because collection ids arrive as a number
// from redux and a string from the route params.
const assetsInOrder = (assets, collectionId) => (Array.isArray(assets) ? assets : [])
    .filter(asset => asset != null
        && asset.deleted !== true
        && String(asset.collection_id) === String(collectionId));

const SCAN_OUTCOME = {
    MATCH: 'match',            // on the list, not yet collected -> collect it
    DUPLICATE: 'duplicate',    // already collected -> notify, reject, don't count
    UNEXPECTED: 'unexpected',  // not on the list -> create it (highlight yellow)
    AMBIGUOUS: 'ambiguous',    // more than one candidate -> refuse, don't guess
    EMPTY: 'empty'             // nothing to go on
};

const MESSAGES = {
    [SCAN_OUTCOME.EMPTY]: 'Nothing scanned. Please scan or enter a serial number.',
    'ambiguous-complete': 'Collection Failed! Please provide a unique Serial Number and Asset Tag.',
    'ambiguous-tag': 'Collection Failed! Please provide a unique Asset Tag.',
    'ambiguous-serial': 'Collection Failed! Please provide a unique Serial Number.',
    [SCAN_OUTCOME.DUPLICATE]: 'Duplicate Scan — this item is already collected on this order.'
};

const decision = (outcome, extra = {}) => ({
    outcome,
    asset: null,
    assetId: null,
    matchedBy: null,
    candidateIds: [],
    duplicateOfId: null,
    serialKey: '',
    tagKey: '',
    reason: null,
    message: MESSAGES[outcome] ?? null,
    ...extra
});

/**
 * Classify one scan against one order's assets.
 *
 *   serial       - the scanned or typed serial number
 *   assetTag     - the asset tag currently in the form, if any
 *   assets       - the loaded asset rows (any collection; filtered here)
 *   collectionId - the order being scanned
 *
 * Returns a ScanDecision. Never mutates its inputs, and never depends on the
 * order of `assets`.
 */
const classifyScan = ({ serial, assetTag, assets, collectionId } = {}) => {
    const sKey = serialKey(serial);
    const tKey = tagKey(assetTag);
    const base = { serialKey: sKey, tagKey: tKey };

    if (sKey === '' && tKey === '') {
        return decision(SCAN_OUTCOME.EMPTY, { ...base, reason: 'blank-input' });
    }

    const inOrder = assetsInOrder(assets, collectionId);

    const serialMatches = sKey === '' ? [] : inOrder.filter(a => serialKey(a.serial_number) === sKey);
    const tagMatches = tKey === '' ? [] : inOrder.filter(a => tagKey(a.asset_tag) === tKey);
    const completeMatches = inOrder.filter(a => sKey !== '' && tKey !== ''
        && serialKey(a.serial_number) === sKey && tagKey(a.asset_tag) === tKey);

    const ids = (rows) => rows.map(row => row.id);

    // The resolution ladder, in the same precedence the old collectAsset used --
    // most specific evidence first -- but normalised and order-independent.
    let winner = null;
    let matchedBy = null;

    if (completeMatches.length === 1) {
        winner = completeMatches[0];
        matchedBy = 'serial+tag';
    } else if (completeMatches.length > 1) {
        return decision(SCAN_OUTCOME.AMBIGUOUS, {
            ...base, reason: 'ambiguous-complete', candidateIds: ids(completeMatches),
            message: MESSAGES['ambiguous-complete']
        });
    } else if (tagMatches.length === 1 && serialMatches.length === 0) {
        winner = tagMatches[0];
        matchedBy = 'tag';
    } else if (tagMatches.length > 1 && serialMatches.length === 0) {
        return decision(SCAN_OUTCOME.AMBIGUOUS, {
            ...base, reason: 'ambiguous-tag', candidateIds: ids(tagMatches),
            message: MESSAGES['ambiguous-tag']
        });
    } else if (serialMatches.length === 1) {
        winner = serialMatches[0];
        matchedBy = 'serial';
    } else if (serialMatches.length > 1) {
        // Two rows in one order holding the same serial. That should be impossible
        // once P1-4 rejects such an import, but legacy data predates the rule, so
        // refuse rather than pick one -- picking silently collects an arbitrary row.
        return decision(SCAN_OUTCOME.AMBIGUOUS, {
            ...base, reason: 'ambiguous-serial', candidateIds: ids(serialMatches),
            message: MESSAGES['ambiguous-serial']
        });
    }

    if (winner == null) {
        // Nothing on this order's list matches, so it is a genuine onsite find.
        return decision(SCAN_OUTCOME.UNEXPECTED, { ...base, reason: null, message: null });
    }

    if (isCollected(winner)) {
        // The one duplicate check, reading the row that was actually resolved.
        // Note this catches a re-scan of an already-collected UNEXPECTED row too,
        // which is why the create path needs no duplicate check of its own: it is
        // never reached for one.
        return decision(SCAN_OUTCOME.DUPLICATE, {
            ...base,
            asset: winner,
            assetId: winner.id,
            matchedBy,
            candidateIds: [winner.id],
            duplicateOfId: winner.id
        });
    }

    return decision(SCAN_OUTCOME.MATCH, {
        ...base,
        asset: winner,
        assetId: winner.id,
        matchedBy,
        candidateIds: [winner.id],
        message: null
    });
};

const exports = {
    SCAN_OUTCOME,
    serialKey,
    tagKey,
    isCollected,
    assetsInOrder,
    classifyScan,
};

export default exports;
export {
    SCAN_OUTCOME,
    serialKey,
    tagKey,
    isCollected,
    assetsInOrder,
    classifyScan,
};
